import { useState, useEffect } from "react";
import { NavLink, Outlet } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useBranch } from "../context/BranchContext";
import { Logo } from "./shared";
import api, { apiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import {
  LayoutDashboard, CalendarClock, Receipt, Users, Scissors, Clock,
  Wallet, ListChecks, Building2, PiggyBank, BarChart3,
  ScrollText, Bot, Settings as SettingsIcon, LogOut, Menu, X, Gift, Package, Boxes, MessageCircle, KeyRound, Sparkles, UserPlus, Shield, Send, Image as ImageIcon,
} from "lucide-react";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/appointments", label: "Appointments", icon: CalendarClock },
  { to: "/billing", label: "Billing", icon: Receipt },
  { to: "/customers", label: "Customers", icon: Users },
  { to: "/enquiries", label: "Enquiries", icon: UserPlus },
  { to: "/staff", label: "Staff", icon: Scissors },
  { to: "/attendance", label: "Attendance", icon: Clock },
  { to: "/salary", label: "Salary", icon: Wallet },
  { to: "/services", label: "Services", icon: ListChecks },
  { to: "/offers", label: "Offers", icon: Gift, roles: ["OWNER", "SUPER_ADMIN"] },
  { to: "/products", label: "Products", icon: Package },
  { to: "/inventory", label: "Inventory", icon: Boxes },
  { to: "/whatsapp", label: "WhatsApp", icon: MessageCircle },
  { to: "/whatsapp-ai", label: "WhatsApp AI", icon: Bot, badge: "wa" },
  { to: "/packages", label: "Packages", icon: Gift },
  { to: "/branches", label: "Branches", icon: Building2, roles: ["OWNER", "SUPER_ADMIN"] },
  { to: "/booking-site", label: "Booking Site", icon: Sparkles },
  { to: "/website-images", label: "Website Images", icon: ImageIcon },
  { to: "/expenses", label: "Expenses", icon: PiggyBank },
  { to: "/reports", label: "Reports", icon: BarChart3 },
  { to: "/settings", label: "Settings", icon: SettingsIcon, roles: ["OWNER", "SUPER_ADMIN"] },
];

export default function Layout() {
  const { user, logout } = useAuth();  const { branches, selected, setSelected, allowAll, allLabel } = useBranch();
  const [open, setOpen] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
  const [savingPw, setSavingPw] = useState(false);
  const [waCount, setWaCount] = useState(0);
  useEffect(() => {
    let stop = false;
    const tick = () => api.get("/wa-ai/unanswered/count").then((r) => { if (!stop) setWaCount(r.data?.count || 0); }).catch(() => {});
    tick();
    const id = setInterval(tick, 60000);
    return () => { stop = true; clearInterval(id); };
  }, []);
  const changePassword = async () => {
    if (pw.next.length < 6) return toast.error("New password must be at least 6 characters");
    if (pw.next !== pw.confirm) return toast.error("Passwords do not match");
    setSavingPw(true);
    try {
      await api.post("/auth/change-password", { current_password: pw.current, new_password: pw.next });
      toast.success("Password changed successfully");
      setPwOpen(false); setPw({ current: "", next: "", confirm: "" });
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSavingPw(false); }
  };
  const isAdmin = ["OWNER", "SUPER_ADMIN"].includes(user?.role);
  const isOwner = user?.role === "OWNER";

  const perms = user?.permissions || {};
  const NAV_PERM = {
    "/": "dashboard_view", "/appointments": "appointment_view", "/billing": "billing_view",
    "/customers": "customer_view", "/staff": ["staff_view", "add_staff", "edit_staff"], "/attendance": "attendance_view",
    "/enquiries": "enquiry_view",
    "/salary": "salary_view", "/incentives": "incentive_view", "/services": "services_view",
    "/menu": "service_menu", "/products": "products_view", "/inventory": "inventory_view",
    "/whatsapp": ["whatsapp_access", "whatsapp_chat_view", "whatsapp_chat_send"], "/whatsapp-ai": "whatsapp_access", "/campaigns": ["campaign_view", "campaign_create", "campaign_send", "campaign_schedule", "campaign_reports", "send_whatsapp_campaign"], "/packages": "packages_view", "/expenses": "expenses_view",
    "/reports": ["reports_view", "expense_report"], "/audit": "audit_log_view", "/ai": "ai_assistant", "/settings": "settings_view", "/booking-site": "booking_site_manage", "/website-images": "booking_site_manage",
  };
  const items = NAV.filter((n) => {
    if (n.roles && !n.roles.includes(user?.role)) return false;
    // Hard privacy rule: Receptionists never see the Staff management section.
    if (n.to === "/staff" && user?.role === "RECEPTION") return false;
    if (isAdmin) return true;
    const p = NAV_PERM[n.to];
    // Non-admins: hide any permission-gated item until effective permissions are known,
    // then require the specific permission. Ungated items always show.
    if (!p) return true;
    if (!user?.permissions) return false;
    return Array.isArray(p) ? p.some((x) => perms[x]) : !!perms[p];
  });

  const handleLogout = () => { logout(); };

  return (
    <div className="min-h-screen flex bg-background">
      {/* Sidebar */}
      <aside
        className={`fixed lg:static z-40 h-full w-64 bg-[#12201A] border-r border-border flex flex-col transition-transform duration-200 ${open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}
      >
        <div className="h-16 flex items-center px-6 border-b border-border">
          <div>
            <Logo className="text-xl" />
            <p className="text-[10px] uppercase tracking-[0.25em] text-primary/80 mt-0.5" data-testid="app-name">Dynamic Billing</p>
          </div>
        </div>
        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1">
          {items.map((n) => {
            const Icon = n.icon;
            return (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                onClick={() => setOpen(false)}
                data-testid={`nav-${n.label.toLowerCase().replace(/ /g, "-")}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2 rounded-sm text-sm transition-colors duration-150 ${isActive ? "bg-primary text-primary-foreground font-medium" : "text-muted-foreground hover:bg-secondary hover:text-foreground"}`
                }
              >
                <Icon size={17} />
                <span className="flex-1">{n.label}</span>
                {n.badge === "wa" && waCount > 0 && (
                  <span data-testid="nav-wa-unanswered-badge" className="ml-auto min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">{waCount}</span>
                )}
              </NavLink>
            );
          })}
        </nav>
        <div className="p-3 border-t border-border space-y-1">
          <button onClick={() => setPwOpen(true)} data-testid="change-password-btn"
            className="flex items-center gap-3 px-3 py-2 rounded-sm text-sm text-muted-foreground hover:text-foreground w-full">
            <KeyRound size={17} /> Change Password
          </button>
          <button onClick={handleLogout} data-testid="logout-btn"
            className="flex items-center gap-3 px-3 py-2 rounded-sm text-sm text-muted-foreground hover:text-destructive w-full">
            <LogOut size={17} /> Logout
          </button>
        </div>
      </aside>

      {open && <div className="fixed inset-0 bg-black/50 z-30 lg:hidden" onClick={() => setOpen(false)} />}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 sticky top-0 z-20 backdrop-blur-xl bg-background/80 border-b border-border flex items-center justify-between px-4 sm:px-6">
          <div className="flex items-center gap-3">
            <button className="lg:hidden text-foreground" onClick={() => setOpen(!open)}>
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
            <div className="w-48 sm:w-64">
              <Select value={selected} onValueChange={setSelected}>
                <SelectTrigger data-testid="branch-switcher" className="h-9 bg-secondary border-border">
                  <SelectValue placeholder="Branch" />
                </SelectTrigger>
                <SelectContent>
                  {allowAll && <SelectItem value="all">{allLabel}</SelectItem>}
                  {branches.map((b) => (
                    <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right hidden sm:block">
              <p className="text-sm font-medium leading-tight">{user?.name}</p>
              <p className="text-xs text-primary tracking-wide">{user?.role}</p>
            </div>
            <div className="h-9 w-9 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-medium">
              {user?.name?.[0]?.toUpperCase()}
            </div>
          </div>
        </header>
        <main className="flex-1 p-4 sm:p-6 lg:p-8 overflow-x-hidden">
          <Outlet />
        </main>
      </div>

      <Dialog open={pwOpen} onOpenChange={setPwOpen}>
        <DialogContent className="bg-card border-border max-w-sm" data-testid="change-password-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">Change Password</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Current password</Label><Input type="password" value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} className="bg-secondary mt-1" data-testid="pw-current" /></div>
            <div><Label className="text-xs">New password</Label><Input type="password" value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })} className="bg-secondary mt-1" data-testid="pw-new" /></div>
            <div><Label className="text-xs">Confirm new password</Label><Input type="password" value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} className="bg-secondary mt-1" data-testid="pw-confirm" /></div>
            <Button onClick={changePassword} disabled={savingPw} data-testid="pw-save" className="w-full rounded-sm bg-primary text-primary-foreground hover:bg-[#D4AF37]">{savingPw ? "Saving..." : "Update Password"}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
